import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, Button, Table, Space, message, Spin, Alert, Tag } from 'antd';
import { DownloadOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { getTemplate, downloadTemplate } from '../services/api';

// ======================== 使用说明 ========================

const usageNotes: Record<string, string[]> = {
  msa_continuous: [
    '每个零件由每位操作员测量指定次数',
    '随机化模式：零件呈现顺序随机，减少系统误差',
    '教学模式（关闭随机化）：按零件编号顺序测量，方便学员操作',
    '确保测量仪器在整个过程中保持一致',
    '在"测量值"列中填入实际测量数据',
    '数据收集完成后上传至"统计分析"→ MSA 模块进行 GR&R 分析',
  ],
  msa_attribute: [
    '每个零件由每位操作员判定指定次数',
    '判定结果填写 Pass 或 Fail',
    '建议包含约 20% 的已知不合格品（参考标准=Fail）',
    '判定顺序随机化可减少记忆偏差',
    '数据收集完成后可进行 Kappa 一致性分析',
  ],
  doe_full_factorial: [
    '因子列的 -1/+1 对应实际水平的低/高值',
    '在"响应值"列填入每次实验的结果',
    '"标准顺序"是 Yates 标准排列，"运行顺序"是实际执行顺序',
    '教学模式（关闭随机化）：运行顺序=标准顺序，便于理解设计结构',
    '有区组时：不同区组安排在不同天/班次进行',
    '有中心点时："点类型"列标记为"中心点"的行用因子中间值做实验',
    '数据收集完成后上传进行 ANOVA 和效应分析',
  ],
  doe_fractional: [
    '部分因子设计只包含全因子的一部分组合，以减少实验次数',
    'Resolution III：主效应与二阶交互混淆 — 适合初步筛选',
    'Resolution IV：主效应清晰，二阶交互之间混淆 — 推荐',
    'Resolution V：主效应和二阶交互均清晰 — 接近全因子',
    '有中心点时可检测是否存在曲率效应',
    '如检测到曲率，建议升级为 RSM（响应曲面）设计',
  ],
  doe_rsm: [
    'CCD 设计包含三类点：因子点(±1)、轴向点(±α)、中心点(0)',
    'Box-Behnken 不包含轴向点，不需要极端水平组合',
    'α 值已自动设为可旋转设计的最优值',
    '中心点建议 3~5 个，用于估计纯误差和检测曲率',
    '上传后可进行二次回归建模，找到最优响应区域',
  ],
  doe_evop: [
    'EVOP 在正常生产中进行，因子变动幅度要小（不影响产品质量）',
    '每个循环包含 2^k 个因子点 + 1 个中心点',
    '多个循环的数据累积后才能检测出显著效应',
    '建议至少 3 个循环以获得足够的信息',
  ],
  doe_mixture: [
    '各组分比例之和 = 100%，这是硬约束',
    '格子设计：均匀分布在单纯形上，覆盖面广',
    '质心设计：顶点+边中点+质心，点数较少',
    '在"响应值"列填入每个配方的试验结果',
  ],
  doe_taguchi: [
    '系统已根据因子数和水平数自动选择正交表',
    '"正交表"列显示所选的正交表编号（如 L9、L18）',
    '各因子列的数字代表水平编号（1, 2, 3...）',
    '"响应值"填实验结果，"S/N比"在分析时自动计算',
    '田口方法关注信噪比（越大越好/越小越好/望目特性）',
  ],
  spc_xbar_r: [
    '每个子组在同一时间/条件下连续抽取指定数量的样本',
    'X1, X2, ... 列分别填入每个子组内各样本的测量值',
    '建议至少 25 个子组以建立稳定的控制限',
    '采样间隔应固定（如每小时/每班/每批），记录在"采样时间"列',
    '数据收集完成后上传进行 Xbar-R 控制图分析',
  ],
  spc_imr: [
    '每个采样点记录一个测量值（子组大小 = 1）',
    '适用于破坏性检测、昂贵测试、或每批只有一个产品的场景',
    '按时间顺序采集，在"采样时间"列记录时间',
    '建议至少 25 个数据点',
  ],
  spc_attribute: [
    'P图：不良品数 / 样本量 = 不良率（样本量可变）',
    'NP图：不良品数（样本量必须固定）',
    'C图：单件/固定面积上的缺陷数',
    'U图：缺陷数 / 检验单元数 = 单位缺陷率（面积可变）',
    '确保"样本量"列记录准确，这是计算控制限的关键',
  ],
};

// ======================== Component ========================

const TemplatePreview: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const type = searchParams.get('type') || '';
  const params = JSON.parse(searchParams.get('params') || '{}');

  const [loading, setLoading] = useState(true);
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [previewColumns, setPreviewColumns] = useState<any[]>([]);

  useEffect(() => {
    const loadPreview = async () => {
      try {
        await getTemplate(type);
      } catch { /* fallback below */ }

      const cols = generatePreviewColumns(type, params);
      const rows = generatePreviewRows(type, params, cols);
      setPreviewColumns(cols.map((c: string) => ({
        title: c,
        dataIndex: c,
        key: c,
        width: c.length > 4 ? 130 : 100,
        render: (val: any) => {
          if (val === '') return <span style={{ color: '#d9d9d9' }}>待填写</span>;
          return val;
        },
      })));
      setPreviewData(rows);
      setLoading(false);
    };
    loadPreview();
  }, [type]);

  const handleDownload = async (format: 'xlsx' | 'csv') => {
    try {
      const blob = await downloadTemplate(type, { ...params, format });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `template_${type}.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);
      message.success(`模板已下载为 ${format.toUpperCase()} 格式`);
    } catch {
      message.warning('下载功能需要后端服务支持');
    }
  };

  const notes = usageNotes[type] || ['按模板中的列定义填写数据', '不要修改表头结构', '数据收集完成后上传至平台进行分析'];

  // 运行次数摘要
  const runInfo = getRunInfo(type, params);

  return (
    <div>
      <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/templates')} style={{ marginBottom: 16 }}>
        返回模板列表
      </Button>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 100 }}>
          <Spin size="large" tip="正在生成模板预览..." />
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 24 }}>
          <Card
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color: '#061b31', fontWeight: 600 }}>数据模板预览</span>
                {runInfo && <Tag color="purple">{runInfo}</Tag>}
              </div>
            }
            style={{ flex: 1 }}
          >
            <Table
              columns={previewColumns}
              dataSource={previewData}
              rowKey="key"
              size="small"
              scroll={{ x: previewColumns.length * 110, y: 500 }}
              pagination={false}
              bordered
            />
          </Card>

          <div style={{ width: 320, flexShrink: 0 }}>
            <Card
              title={<span style={{ color: '#061b31', fontWeight: 600 }}>使用说明</span>}
              style={{ marginBottom: 16 }}
            >
              <Alert
                message="数据收集规范"
                description={
                  <ul style={{ paddingLeft: 20, margin: '8px 0 0' }}>
                    {notes.map((note, i) => (
                      <li key={i} style={{ marginBottom: 6, fontSize: 13, lineHeight: 1.6 }}>
                        {note}
                      </li>
                    ))}
                  </ul>
                }
                type="info"
                showIcon
              />
            </Card>

            <Card title={<span style={{ color: '#061b31', fontWeight: 600 }}>下载模板</span>}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button type="primary" icon={<DownloadOutlined />} block size="large" onClick={() => handleDownload('xlsx')}>
                  下载 Excel (.xlsx)
                </Button>
                <Button icon={<DownloadOutlined />} block size="large" onClick={() => handleDownload('csv')}>
                  下载 CSV (.csv)
                </Button>
              </Space>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

// ======================== Helper functions ========================

function getRunInfo(type: string, params: Record<string, any>): string | null {
  switch (type) {
    case 'msa_continuous':
      return `${(params.parts || 10) * (params.operators || 3) * (params.trials || 3)} 次测量`;
    case 'msa_attribute':
      return `${(params.parts || 50) * (params.operators || 3) * (params.trials || 3)} 次判定`;
    case 'doe_full_factorial':
      return `${Math.pow(params.levels || 2, params.factors || 3) * (params.replicates || 1) + (params.center_points || 0)} 次运行`;
    case 'doe_fractional':
      return '部分因子设计';
    case 'spc_xbar_r':
      return `${params.subgroups || 25} 子组 × ${params.subgroup_size || 5}`;
    case 'spc_imr':
      return `${params.samples || 30} 个采样点`;
    case 'spc_attribute':
      return `${params.subgroups || 25} 批 × ${params.sample_size || 100}`;
    default:
      return null;
  }
}

function generatePreviewColumns(type: string, params: Record<string, any>): string[] {
  switch (type) {
    case 'msa_continuous':
      return ['测量顺序', '零件编号', '操作员', '试验次数', '测量值'];
    case 'msa_attribute':
      return ['判定顺序', '零件编号', '操作员', '试验次数', '判定结果', '参考标准'];

    case 'doe_full_factorial':
    case 'doe_fractional': {
      const f = params.factors || 3;
      const cols = ['标准顺序', '运行顺序'];
      if ((params.blocks || 1) > 1) cols.push('区组');
      cols.push('点类型');
      for (let i = 0; i < f; i++) cols.push(`因子${String.fromCharCode(65 + i)}`);
      cols.push('响应值');
      return cols;
    }
    case 'doe_rsm': {
      const f = params.factors || 2;
      const cols = ['标准顺序', '运行顺序', '点类型'];
      for (let i = 0; i < f; i++) cols.push(`因子${String.fromCharCode(65 + i)}`);
      cols.push('响应值');
      return cols;
    }
    case 'doe_evop': {
      const f = params.factors || 2;
      const cols = ['循环', '运行顺序'];
      for (let i = 0; i < f; i++) cols.push(`因子${String.fromCharCode(65 + i)}`);
      cols.push('响应值');
      return cols;
    }
    case 'doe_mixture': {
      const n = params.components || 3;
      const cols = ['标准顺序', '运行顺序'];
      for (let i = 0; i < n; i++) cols.push(`组分${String.fromCharCode(65 + i)}`);
      cols.push('响应值');
      return cols;
    }
    case 'doe_taguchi': {
      const f = params.factors || 4;
      const cols = ['标准顺序', '运行顺序', '正交表'];
      for (let i = 0; i < f; i++) cols.push(`因子${String.fromCharCode(65 + i)}`);
      cols.push('响应值', 'S/N比');
      return cols;
    }

    case 'spc_xbar_r': {
      const ss = params.subgroup_size || 5;
      const cols = ['子组号', '采样时间'];
      for (let i = 1; i <= ss; i++) cols.push(`X${i}`);
      return cols;
    }
    case 'spc_imr':
      return ['采样点', '采样时间', '测量值'];
    case 'spc_attribute': {
      const ct = params.chart_type || 'p_chart';
      const cols = ['批次号', '检验日期', '样本量'];
      if (ct === 'p_chart' || ct === 'np_chart') cols.push('不良数');
      else if (ct === 'c_chart') cols.push('缺陷数');
      else cols.push('缺陷数', '检验单元数');
      return cols;
    }

    default:
      return ['序号', '数据'];
  }
}

function generatePreviewRows(type: string, params: Record<string, any>, columns: string[]): Record<string, any>[] {
  const rows: Record<string, any>[] = [];
  const numRows = Math.min(15, getExpectedRows(type, params));
  const randomize = params.randomize !== 'no';

  for (let i = 0; i < numRows; i++) {
    const row: Record<string, any> = { key: i };
    columns.forEach((col) => {
      // Index columns
      if (['标准顺序', '采样点', '子组号', '批次号'].includes(col)) row[col] = i + 1;
      else if (col === '运行顺序') row[col] = randomize ? [3, 7, 1, 5, 2, 8, 4, 6, 9, 11, 10, 12, 13, 15, 14][i % 15] : i + 1;
      else if (col === '测量顺序' || col === '判定顺序') row[col] = randomize ? [5, 2, 8, 1, 9, 3, 7, 4, 6, 10, 12, 11, 14, 13, 15][i % 15] : i + 1;
      else if (col === '循环') row[col] = Math.floor(i / (Math.pow(2, params.factors || 2) + 1)) + 1;
      else if (col === '正交表') row[col] = 'L9';
      // MSA
      else if (col === '零件编号') row[col] = `P${(i % (params.parts || 10)) + 1}`;
      else if (col === '操作员') row[col] = `操作员${(Math.floor(i / (params.trials || 3)) % (params.operators || 3)) + 1}`;
      else if (col === '试验次数') row[col] = (i % (params.trials || 3)) + 1;
      else if (col === '参考标准') row[col] = i % 5 === 0 ? 'Fail' : 'Pass';
      // DOE
      else if (col === '点类型') row[col] = '因子点';
      else if (col === '区组') row[col] = 1;
      else if (col.startsWith('因子')) {
        const levels = params.levels || 2;
        row[col] = levels === 2 ? (i % 2 === 0 ? -1 : 1) : (i % levels) + 1;
      }
      else if (col.startsWith('组分')) row[col] = '';
      // SPC
      else if (col === '样本量') row[col] = params.sample_size || 100;
      else if (col === '采样时间' || col === '检验日期') row[col] = '';
      // Empty value columns
      else row[col] = '';
    });
    rows.push(row);
  }
  return rows;
}

function getExpectedRows(type: string, params: Record<string, any>): number {
  switch (type) {
    case 'msa_continuous':
      return (params.parts || 10) * (params.operators || 3) * (params.trials || 3);
    case 'msa_attribute':
      return (params.parts || 50) * (params.operators || 3) * (params.trials || 3);
    case 'doe_full_factorial':
      return Math.pow(params.levels || 2, params.factors || 3) * (params.replicates || 1) + (params.center_points || 0);
    case 'doe_fractional':
      return 16;
    case 'doe_rsm':
      return Math.pow(2, params.factors || 2) + 2 * (params.factors || 2) + (params.center_points || 3);
    case 'doe_taguchi':
      return (params.levels || 3) ** 2;
    case 'spc_xbar_r':
      return params.subgroups || 25;
    case 'spc_imr':
      return params.samples || 30;
    case 'spc_attribute':
      return params.subgroups || 25;
    default:
      return 20;
  }
}

export default TemplatePreview;
